"use client";

import { DynamicTooltipProvider } from "@/components/ui/DynamicTooltip";
import { Toaster } from "@/components/ui/toaster";
import { NextUIProvider } from "@nextui-org/react";
import { api } from "@src/lib/api";
import { ThemeProvider } from "next-themes";
import { useRouter } from "next/navigation";
import "@src/i18n/config";
import { FC, PropsWithChildren, useEffect, useState } from "react";
import { setNeoHost } from "@character-tech/common/src/lib/axiosConstants";
import { ClientEnv } from "@src/env/client";
import { StatsigProvider } from "@statsig/react-bindings";
import { useAuth } from "@src/context/auth/AuthProvider";

const StatsigProviderWrapper: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const [isMounted, setIsMounted] = useState(false);
  const [statsigUser, setStatsigUser] = useState<any>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    const newStatsigUser = user?.user
      ? {
          userID: user.user.id.toString(),
          email: user.email,
          custom: {
            is_staff: user.user.is_staff,
          },
        }
      : {
          userID: "anonymous",
        };

    setStatsigUser(newStatsigUser);
  }, [user, isMounted]);

  if (!isMounted || !statsigUser) {
    return <>{children}</>;
  }

  return (
    <StatsigProvider
      sdkKey={ClientEnv.NEXT_PUBLIC_STATSIG_CLIENT_KEY}
      user={statsigUser}
    >
      {children}
    </StatsigProvider>
  );
};

const ClientProviders: FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();

  useEffect(() => {
    setNeoHost(ClientEnv.NEXT_PUBLIC_NEO_HOST_BASE);
  }, []);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      disableTransitionOnChange
      enableSystem
      enableColorScheme
    >
      <NextUIProvider navigate={router.push}>
        <Toaster />
        <DynamicTooltipProvider>
          <StatsigProviderWrapper>{children}</StatsigProviderWrapper>
        </DynamicTooltipProvider>
      </NextUIProvider>
    </ThemeProvider>
  );
};

export default api.withTRPC(ClientProviders) as FC<PropsWithChildren>;
